import React from "react";
import { MapPin, Phone, Globe, Clock, Star, Navigation, PhoneCall, ExternalLink } from "lucide-react";
import { useWizard, BUSINESS_TYPES, DAYS } from "../lib/wizardStore";

function getCategoryLabel(typeId) {
  const t = BUSINESS_TYPES.find((b) => b.id === typeId);
  return t ? t.category : "Local business";
}

function todayKey() {
  const d = new Date();
  const map = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  return map[d.getDay()];
}

function formatHours(hoursObj) {
  const today = todayKey();
  const h = hoursObj[today];
  if (!h) return { label: "Hours not set", isOpen: false };
  if (h.closed) return { label: "Closed today", isOpen: false };
  if (!h.open || !h.close) return { label: "Hours not set", isOpen: false };
  return { label: `Open · ${h.open} – ${h.close}`, isOpen: true };
}

export function GooglePreviewCard({ compact = false }) {
  const { state } = useWizard();
  const { businessInfo, businessType, services, hours, photos } = state;
  const name = businessInfo.name || "Your name";
  const category = getCategoryLabel(businessType);
  const { label: hoursLabel, isOpen } = formatHours(hours);

  // initials for avatar
  const initials = (name || "G").split(" ").map(w => w[0]).slice(0,2).join("").toUpperCase();

  return (
    <div
      data-testid="google-preview-card"
      className="bg-white rounded-3xl border border-slate-200 overflow-hidden gigma-card-shadow"
    >
      {/* Top bar — mock Google search header */}
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
        <div className="w-5 h-5 rounded-full bg-slate-100 flex items-center justify-center">
          <span className="text-[10px] font-semibold text-slate-500">G</span>
        </div>
        <div className="flex-1 text-xs text-slate-400 truncate">
          {name.toLowerCase().replace(/\s+/g, " ")} {businessInfo.address ? `· ${businessInfo.address.split(",")[0]}` : ""}
        </div>
        <span className="inline-flex items-center gap-1 text-[10px] text-emerald-600">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 gigma-live-dot" />
          Live
        </span>
      </div>

      {/* Map / cover placeholder */}
      <div className="relative h-32 bg-gradient-to-br from-blue-50 via-emerald-50 to-amber-50 overflow-hidden">
        {/* Faux map roads */}
        <svg className="absolute inset-0 w-full h-full opacity-70" viewBox="0 0 400 200" preserveAspectRatio="none">
          <path d="M0 160 Q100 120 200 140 T 400 100" stroke="#cbd5e1" strokeWidth="6" fill="none" strokeLinecap="round" />
          <path d="M40 0 L 80 200" stroke="#e2e8f0" strokeWidth="4" />
          <path d="M260 0 L 300 200" stroke="#e2e8f0" strokeWidth="4" />
        </svg>
        {/* Pin */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <div className="relative">
            <div className="w-8 h-8 rounded-full bg-rose-500 border-4 border-white shadow-md" />
            <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-2 h-2 bg-rose-600 rotate-45" />
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="p-5">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 via-green-500 to-amber-400 flex items-center justify-center text-white font-heading font-semibold text-lg shrink-0">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading text-xl font-semibold text-slate-900 leading-tight truncate" data-testid="preview-business-name">
              {name}
            </h3>
            <div className="flex items-center gap-2 mt-1 text-sm text-slate-600">
              <span className="font-medium text-amber-600 flex items-center gap-0.5">
                4.9
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-3.5 h-3.5 fill-amber-500 text-amber-500" />
                ))}
              </span>
              <span className="text-slate-400">·</span>
              <span className="truncate">{category}</span>
            </div>
            <div className="text-xs text-slate-500 mt-0.5">120 Google reviews</div>
          </div>
        </div>

        {/* Status */}
        <div className="mt-4 flex items-center gap-2 text-sm">
          <Clock className="w-4 h-4 text-slate-400" />
          <span className={isOpen ? "text-emerald-600 font-medium" : "text-slate-500"}>{hoursLabel}</span>
        </div>

        {/* Action pill buttons */}
        <div className="mt-4 flex items-center gap-2 overflow-x-auto no-scrollbar">
          <button className="shrink-0 inline-flex items-center gap-1.5 text-[13px] font-medium text-blue-600 border border-slate-200 rounded-full px-3.5 py-1.5 hover:bg-slate-50">
            <Navigation className="w-3.5 h-3.5" /> Directions
          </button>
          <button className="shrink-0 inline-flex items-center gap-1.5 text-[13px] font-medium text-blue-600 border border-slate-200 rounded-full px-3.5 py-1.5 hover:bg-slate-50">
            <PhoneCall className="w-3.5 h-3.5" /> Call
          </button>
          <button className="shrink-0 inline-flex items-center gap-1.5 text-[13px] font-medium text-blue-600 border border-slate-200 rounded-full px-3.5 py-1.5 hover:bg-slate-50">
            <ExternalLink className="w-3.5 h-3.5" /> Website
          </button>
        </div>

        {/* Details list */}
        <div className="mt-5 space-y-3 text-sm">
          <Row icon={<MapPin className="w-4 h-4 text-slate-400" />} value={businessInfo.address || "Add an address to display here"} muted={!businessInfo.address} />
          <Row icon={<Phone className="w-4 h-4 text-slate-400" />} value={businessInfo.phone || "Add a phone number"} muted={!businessInfo.phone} />
          <Row icon={<Globe className="w-4 h-4 text-slate-400" />} value={businessInfo.website || "Add a website"} muted={!businessInfo.website} />
          {businessInfo.serviceArea && (
            <Row icon={<Navigation className="w-4 h-4 text-slate-400" />} value={`Serves ${businessInfo.serviceArea}`} />
          )}
        </div>

        {!compact && services && services.trim().length > 0 && (
          <div className="mt-5 pt-4 border-t border-slate-100">
            <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Services</div>
            <p className="text-[13px] text-slate-700 leading-relaxed whitespace-pre-wrap" data-testid="preview-services">
              {services}
            </p>
          </div>
        )}

        {!compact && (
          <div className="mt-5 pt-4 border-t border-slate-100">
            <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Photos</div>
            <div className="grid grid-cols-4 gap-1.5">
              {Object.entries(photos).slice(0, 4).map(([k, v]) => (
                <div
                  key={k}
                  className={`aspect-square rounded-lg ${v === "ready" ? "bg-gradient-to-br from-blue-100 to-emerald-100" : "bg-slate-100"} flex items-center justify-center text-[10px] text-slate-400`}
                >
                  {v === "ready" ? "✓" : k.split("_")[0]}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ icon, value, muted }) {
  return (
    <div className="flex items-start gap-2.5">
      <span className="mt-0.5">{icon}</span>
      <span className={`flex-1 ${muted ? "text-slate-400 italic" : "text-slate-700"}`}>{value}</span>
    </div>
  );
}
